/**
 * Helpers for AddFacility to reduce cognitive complexity of performUpdate / form render.
 */

import type { FocusEvent } from 'react';
import type { Facility } from '../types/facility.types';

type NotifyService =
  | {
      show: (opts: {
        title: string;
        message: string;
        type: 'success' | 'error' | 'warning' | 'info';
        duration?: number;
      }) => void;
    }
  | undefined;

export function notifyFacility(
  uiNotificationService: NotifyService,
  success: boolean,
  successMessage: string,
  errorMessage: string
): void {
  if (!uiNotificationService) {
    return;
  }
  uiNotificationService.show({
    title: success ? 'Success' : 'Error',
    message: success ? successMessage : errorMessage,
    type: success ? 'success' : 'error',
    duration: success ? 3000 : 5000,
  });
}

export function resolveFacilityIdForUpdate(
  locationFacility: { id?: number | string } | undefined,
  initFacility: { id?: number | string } | undefined
): number | string | undefined {
  return locationFacility?.id || initFacility?.id;
}

export function buildFacilityUpdateValues<T extends Record<string, unknown>>(
  values: T,
  isAdminView: boolean
): T {
  if (!isAdminView) {
    return values;
  }
  return {
    ...values,
    employeeId: '',
    fullName: '',
    adminPhoneNo: '',
    adminEmailId: '',
    gender: '',
    username: '',
    password: '',
    hasRadiologistHierarchy: values.hasRadiologistHierarchy,
  };
}

export function handlePostFacilityUpdateNavigation(opts: {
  isAdminView: boolean;
  hideFooter: boolean;
  setIsEditable?: (v: boolean) => void;
  onUpdateSuccess?: (data: Facility) => void;
  responseData?: Facility;
  wasEnablingHierarchy: boolean;
  isAdmin: boolean;
  navigate: (path: string) => void;
}): void {
  const {
    isAdminView,
    hideFooter,
    setIsEditable,
    onUpdateSuccess,
    responseData,
    wasEnablingHierarchy,
    isAdmin,
    navigate,
  } = opts;

  if (isAdminView && setIsEditable) {
    setIsEditable(false);
    if (onUpdateSuccess && responseData) {
      onUpdateSuccess(responseData);
    }
    if (wasEnablingHierarchy && isAdmin) {
      navigate('/users');
    }
    return;
  }
  if (hideFooter && setIsEditable) {
    setIsEditable(false);
    if (wasEnablingHierarchy && isAdmin) {
      navigate('/users');
    }
    return;
  }
  if (wasEnablingHierarchy && isAdmin) {
    navigate('/users');
    return;
  }
  navigate('/facilities');
}

export function getFacilityFieldError(
  errors: Record<string, unknown>,
  touched: Record<string, unknown>,
  values: Record<string, unknown>,
  fieldName: string
): string | undefined {
  const error = errors[fieldName];
  const isTouched = touched[fieldName];
  const fieldValue = values[fieldName];
  const hasValue = Array.isArray(fieldValue)
    ? fieldValue.length > 0
    : fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  if (!(isTouched || hasValue) || !error) {
    return undefined;
  }
  return typeof error === 'string' ? error : undefined;
}

export function createFacilityTrimBlurHandler(
  fieldName: string,
  values: Record<string, unknown>,
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void,
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void,
  normalizeCode: (raw: string) => string
): (e: FocusEvent<HTMLInputElement>) => void {
  return (e: FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    if (fieldName === 'password') {
      return;
    }
    const raw = e.target.value;
    if (fieldName === 'code') {
      const norm = normalizeCode(raw);
      if (norm !== values.code) {
        setFieldValue('code', norm, false);
      }
      return;
    }
    const trimmed = raw.trim();
    if (trimmed !== raw) {
      setFieldValue(String(fieldName), trimmed, false);
    }
  };
}

export function resolveApprovedBySelectValue(
  value: string | undefined,
  options: Array<{ value: string; label: string }>
): string {
  if (value && options.some(o => o.value === value)) {
    return value;
  }
  return 'Senior Radiologist';
}

type FacilityServiceLike = {
  updateFacility: (
    id: number | string,
    values: any
  ) => Promise<{ success: boolean; data?: Facility; error?: string; message?: string }>;
  createFacility: (
    values: any
  ) => Promise<{ success: boolean; data?: Facility; error?: string; message?: string }>;
};

type AuthSyncLike = {
  syncStoredFacilityRadiologistHierarchy: (facility: Facility & Record<string, unknown>) => void;
};

export async function executeFacilityUpdate(opts: {
  facilityService: FacilityServiceLike;
  authService: AuthSyncLike;
  uiNotificationService: NotifyService;
  facilityId: number | string;
  valuesToSubmit: any;
  isEdit: boolean;
  initialHasRadiologistHierarchy: string;
  isAdminView: boolean;
  hideFooter: boolean;
  setIsEditable?: (v: boolean) => void;
  onUpdateSuccess?: (data: Facility) => void;
  isAdmin: boolean;
  navigate: (path: string) => void;
}): Promise<void> {
  const response = await opts.facilityService.updateFacility(opts.facilityId, opts.valuesToSubmit);

  if (!response.success) {
    console.error('Failed to update facility:', response.error);
    notifyFacility(
      opts.uiNotificationService,
      false,
      '',
      response.error || 'Failed to update facility'
    );
    return;
  }

  if (response.data) {
    const updated = response.data;
    const hierarchyValue =
      updated.has_radiologist_hierarchy === undefined
        ? opts.valuesToSubmit.hasRadiologistHierarchy === 'Yes'
        : updated.has_radiologist_hierarchy;
    opts.authService.syncStoredFacilityRadiologistHierarchy({
      ...updated,
      id: updated.id ?? opts.facilityId,
      has_radiologist_hierarchy: hierarchyValue,
    });
  }

  notifyFacility(
    opts.uiNotificationService,
    true,
    response.message || 'Facility updated successfully',
    ''
  );

  const wasEnablingHierarchy =
    opts.isEdit &&
    opts.initialHasRadiologistHierarchy === 'No' &&
    opts.valuesToSubmit.hasRadiologistHierarchy === 'Yes';

  handlePostFacilityUpdateNavigation({
    isAdminView: opts.isAdminView,
    hideFooter: opts.hideFooter,
    setIsEditable: opts.setIsEditable,
    onUpdateSuccess: opts.onUpdateSuccess,
    responseData: response.data,
    wasEnablingHierarchy,
    isAdmin: opts.isAdmin,
    navigate: opts.navigate,
  });
}

export async function executeFacilityCreate(opts: {
  facilityService: FacilityServiceLike;
  uiNotificationService: NotifyService;
  values: any;
  navigate: (path: string) => void;
}): Promise<void> {
  const response = await opts.facilityService.createFacility(opts.values);

  if (!response.success) {
    console.error('Failed to create facility:', response.error);
    notifyFacility(
      opts.uiNotificationService,
      false,
      '',
      response.error || 'Failed to create facility'
    );
    return;
  }

  notifyFacility(
    opts.uiNotificationService,
    true,
    response.message || 'Facility created successfully',
    ''
  );
  opts.navigate('/facilities');
}
